import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { FileSignature } from "lucide-react";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
import ComplianceProofButton from "../../design-system/compliance/ComplianceProofButton";
// Release C-2 (P1) — Open in Microsoft 365 Mail for this record surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

// B5 part 2c: read-only list of every contract (legacy + built-in e-sign). New contracts are generated and sent for
// signature from the opportunity page (one path: /api/sales); the legacy create/send routes answer 410. Existing
// records, their documents and signing links already issued stay available.
export default function Contracts() {
  const [items, setItems] = useState([]);
  const [attachId, setAttachId] = useState(null);
  const [attachReload, setAttachReload] = useState(0);

  useEffect(() => {
    api.get("/contracts").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  return (
    <AppShell title="Contracts & E-Sign" subtitle={`${items.length} contract(s) · read-only list`}>
      <p className="mb-3 text-sm bg-erf-creamSecondary border border-erf-borderCream rounded p-3" role="note" data-testid="ctr-one-path-note">
        Generate contracts and signing links from the opportunity page (Proposal → contract → project), using your{" "}
        <Link to="/crm/contract-templates" className="underline" data-testid="ctr-templates-link">contract templates</Link>.
        Older contracts made with the previous tool are listed as “Legacy” and stay readable; signing links already sent for them still work.
      </p>
      {items.length === 0 ? (
        <EmptyState icon={FileSignature} title="No contracts yet" subtitle="Contracts are generated from accepted proposals on the opportunity page." />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2 mono">Contract</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Provider</th>
                <th className="text-left px-3 py-2">Signers</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-erf-borderCream/60" data-testid={`ctr-row-${c.id}`}>
                  <td className="px-3 py-2 mono text-[11px]">{c.opportunity_id ? <Link to={`/crm/opportunities/${c.opportunity_id}`} className="underline inline-flex min-h-[44px] items-center" data-testid={`ctr-open-${c.id}`}>{c.id.slice(0, 8)}</Link> : c.id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-[11px]" data-testid={`ctr-kind-${c.id}`}>{c.esign_model === 2 ? "Built-in e-sign" : "Legacy"}</td>
                  <td className="px-3 py-2"><StatusChip status={c.status === "fully_signed" ? "verified" : c.status === "sent_for_signature" ? "connecting" : "not_configured"} label={c.status} /></td>
                  <td className="px-3 py-2 mono text-[11px]">{c.esign_model === 2 ? "built-in" : c.signature_provider}</td>
                  <td className="px-3 py-2 mono text-[11px]">
                    {(c.signers || []).map((s) => (
                      <div key={s.id || s.email}>
                        {s.signed_at ? "✓" : "•"} {s.email || s.name}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* FINAL-POLISH P2 — Row-level icon-only Open in Outlook (Owner/Admin). */}
                    <span className="inline-block align-middle mr-1">
                      <OpenInMicrosoftMailButton
                        surface="contract_row"
                        recordType="contract"
                        recordId={c.id}
                        iconOnly
                      />
                    </span>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setAttachId(attachId === c.id ? null : c.id)} data-testid={`ctr-docs-${c.id}`}>Docs</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {attachId && (
        <div className="mt-4 bg-white border border-erf-borderCream rounded-md p-4" data-testid="ctr-attach-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-nav text-erf-dark/60">Signed documents + change orders for contract {attachId.slice(0, 8)}</div>
            <div className="flex items-center gap-2">
              <ComplianceProofButton recordType="contract" recordId={attachId} />
              <OpenInMicrosoftMailButton surface="contract_detail" recordType="contract" recordId={attachId} />
              <InformationIntake
                recordType="contract" recordId={attachId}
                category="other" confidentiality="restricted"
                capabilities={["upload", "multi", "scan", "existing", "request_info"]}
                onChanged={() => setAttachReload((k) => k + 1)}
              />
            </div>
          </div>
          <AttachmentsPanel recordType="contract" recordId={attachId} reload={attachReload} />
        </div>
      )}
    </AppShell>
  );
}
